import { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  InputAdornment,
  IconButton,
  Popper,
} from "@mui/material";
import SKURow from "./sku-row";

import { wmsService } from "../../services/wmsService";
import CalendarIcon from "@mui/icons-material/CalendarMonthOutlined";
import dayjs from "dayjs";
import Calendar from "react-calendar";

const CreateASN = ({ m2oData, onM2ODataChange, errors, params, clientId }) => {
  const [showDispatchCalendar, setShowDispatchCalendar] = useState(false);
  const [showShipmentCalendar, setShowShipmentCalendar] = useState(false);
  const dispatchCalendarRef = useRef(null);
  const shipmentCalendarRef = useRef(null);
  const iconButtonRef = useRef(null);
  const [wmsProviders, setWMSProviders] = useState([]);

  // Refs to track the calendar elements
  const dispatchPopperRef = useRef(null);
  const shipmentPopperRef = useRef(null);

  const handleBasicInfoChange = (field, value) => {
    onM2ODataChange({
      ...m2oData,
      [field]: value,
    });
  };

  const handleSKUUpdate = (index, updatedSKU) => {
    const updatedSKUs = [...m2oData.sku_list];
    updatedSKUs[index] = updatedSKU;
    onM2ODataChange({
      ...m2oData,
      sku_list: updatedSKUs,
    });
  };

  const handleSKURemove = (index) => {
    if (m2oData.sku_list.length <= 1) return;
    const updatedSKUs = m2oData.sku_list.filter((_, i) => i !== index);
    onM2ODataChange({
      ...m2oData,
      sku_list: updatedSKUs,
    });
  };

  const handleAddSKU = () => {
    const newSKU = {
      product: null,
      sku: "",
      variant_id: "",
      quantity_requested: "",
      quantity_accepted: "",
      price: "",
      instructions: "",
      attachment: null,
      attachmentUrl: null,
      no_of_overpacks: "",
      overpack_type: "",
      items_per_overpack: "",
    };
    const updatedSKUs = [...m2oData.sku_list, newSKU];
    onM2ODataChange({
      ...m2oData,
      sku_list: updatedSKUs,
    });
  };

  const formatDateForInput = (isoDateString) => {
    if (!isoDateString) return "";
    try {
      return dayjs(isoDateString).format("YYYY-MM-DD");
    } catch (e) {
      console.error("Error parsing date:", e);
      return "";
    }
  };

  const parseDateForCalendar = (date) => {
    if (!date) return new Date();
    try {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        return new Date();
      }
      return parsedDate;
    } catch (e) {
      console.error("Error parsing date for calendar:", e);
      return new Date();
    }
  };

  useEffect(() => {
    if (!m2oData.client_id?._id) return;
    fetchWMSProviders(m2oData.client_id?._id);
  }, [m2oData.client_id, params]);

  const fetchWMSProviders = async (clientId) => {
    try {
      const res = await wmsService.fetch(clientId);
      setWMSProviders(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  // Handle clicks outside to close calendars
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check for dispatch calendar
      if (
        showDispatchCalendar &&
        dispatchCalendarRef.current &&
        dispatchPopperRef.current &&
        !dispatchCalendarRef.current.contains(event.target) &&
        !dispatchPopperRef.current.contains(event.target)
      ) {
        setShowDispatchCalendar(false);
      }

      // Check for shipment calendar
      if (
        showShipmentCalendar &&
        shipmentCalendarRef.current &&
        shipmentPopperRef.current &&
        !shipmentCalendarRef.current.contains(event.target) &&
        !shipmentPopperRef.current.contains(event.target)
      ) {
        setShowShipmentCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDispatchCalendar, showShipmentCalendar]);

  return (
    <Box sx={{ fontFamily: "'Poppins', sans-serif" }}>
      <Typography
        fontWeight={600}
        fontFamily={"poppins"}
        textAlign={"left"}
        variant="h6"
        sx={{ textAlign: "left", fontFamily: "'Poppins', sans-serif" }}
      >
        Create P.O.
      </Typography>
      <Box
        sx={{
          p: 3,
          mb: 3,
          border: "1px solid",
          borderColor: "grey.200",
          borderRadius: 1,
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{ textAlign: "left", fontFamily: "'Poppins', sans-serif" }}
        >
          1. Basic Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} sx={{ textAlign: "left" }}>
            <FormLabel
              sx={{
                mb: 1,
                fontSize: "0.875rem",
                fontFamily: "'Poppins', sans-serif",
              }}
              required
            >
              P.O. Number
            </FormLabel>
            <TextField
              style={{ marginTop: "8px" }}
              fullWidth
              size="small"
              value={m2oData?.asn_no || ""}
              onChange={(e) => handleBasicInfoChange("asn_no", e.target.value)}
              placeholder="Enter P.O. Number"
              error={!!errors?.asn_no}
              helperText={errors?.asn_no}
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: "'Poppins', sans-serif",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ textAlign: "left" }}>
            <FormLabel
              sx={{
                mb: 1,
                fontSize: "0.875rem",
                fontFamily: "'Poppins', sans-serif",
              }}
              required
            >
              Master Purchase Order
            </FormLabel>

            <TextField
              fullWidth
              size="small"
              placeholder="Enter purchase order number"
              value={m2oData.purchase_order || ""}
              onChange={(e) =>
                handleBasicInfoChange("purchase_order", e.target.value)
              }
              error={!!errors?.purchase_order}
              helperText={errors?.purchase_order}
              sx={{
                backgroundColor: "#FFF",
                "& .MuiInputBase-input": {
                  fontFamily: "'Poppins', sans-serif",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6} sx={{ textAlign: "left" }}>
            <FormLabel
              sx={{
                mb: 1,
                fontSize: "0.875rem",
                fontFamily: "'Poppins', sans-serif",
              }}
              required
            >
              Supplier
            </FormLabel>

            <TextField
              fullWidth
              size="small"
              placeholder="Enter supplier name"
              value={m2oData.supplier || ""}
              onChange={(e) =>
                handleBasicInfoChange("supplier", e.target.value)
              }
              error={!!errors?.supplier}
              helperText={errors?.supplier}
              sx={{
                backgroundColor: "#FFF",
                "& .MuiInputBase-input": {
                  fontFamily: "'Poppins', sans-serif",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ textAlign: "left" }}>
            <FormLabel
              sx={{
                mb: 1,
                fontSize: "0.875rem",
                fontFamily: "'Poppins', sans-serif",
              }}
              required
            >
              Expected Dispatch Date
            </FormLabel>
            <TextField
              placeholder="Expected dispatch date"
              inputRef={dispatchCalendarRef}
              autoComplete="off"
              name="dispatch-date-field"
              margin="dense"
              size="small"
              value={formatDateForInput(m2oData.expected_dispatch_date)}
              onClick={() => setShowDispatchCalendar(!showDispatchCalendar)}
              fullWidth
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      onClick={() =>
                        setShowDispatchCalendar(!showDispatchCalendar)
                      }
                    >
                      <CalendarIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ backgroundColor: "#FFF" }}
              error={!!errors?.expected_dispatch_date}
              helperText={errors?.expected_dispatch_date}
            />
            <Popper
              style={{ zIndex: 9000 }}
              open={showDispatchCalendar}
              anchorEl={dispatchCalendarRef.current}
              placement="bottom-start"
              disablePortal
              modifiers={[
                {
                  name: "offset",
                  options: {
                    offset: [0, 4],
                  },
                },
              ]}
            >
              <Box
                ref={dispatchPopperRef}
                sx={{
                  bgcolor: "white",
                  boxShadow: 3,
                  borderRadius: 1,
                  p: 1,
                  zIndex: 9000,
                  width: 300,
                  "& .react-calendar": {
                    width: "100%",
                  },
                }}
              >
                <Calendar
                  value={parseDateForCalendar(m2oData.expected_dispatch_date)}
                  onChange={(e) => {
                    handleBasicInfoChange("expected_dispatch_date", e);
                    setShowDispatchCalendar(false);
                  }}
                  className="custom-calendar"
                  defaultActiveStartDate={parseDateForCalendar(
                    m2oData.expected_dispatch_date
                  )}
                  minDate={new Date()}
                />
              </Box>
            </Popper>
          </Grid>
        </Grid>
      </Box>
      <Box
        sx={{
          p: 3,
          mb: 3,
          border: "1px solid",
          borderColor: "grey.200",
          borderRadius: 1,
          textAlign: "right",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{ textAlign: "left", fontFamily: "'Poppins', sans-serif" }}
          >
            SKU Information
          </Typography>
        </Box>
        {m2oData.sku_list?.map((sku, index) => (
          <SKURow key={sku.sku + index} sku={sku} m2oData={m2oData} />
        ))}
      </Box>
    </Box>
  );
};

export default CreateASN;
